import { BlockOutlined } from '@mui/icons-material';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const CHART_DIMENSIONS = {
  big: {
    height: 400,
    width: 800,
  },
  default: {
    height: 200,
    width: 800,
  },
  small: {
    height: 200,
    width: 462,
  },
};

export type ChartContainerProps = {
  children: React.ReactNode;
  description: string;
  dimension?: keyof typeof CHART_DIMENSIONS;
  empty?: boolean;
  title: string;
};

const Container = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.borderColors.default}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1),
  width: '100%',
}));

const Title = styled('h2')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.15rem',
  margin: '0 8px',
}));

const Description = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.85rem',
  margin: '0 8px',
}));

const Message = styled('span')({
  fontSize: '0.9rem',
  textAlign: 'center',
});

const ChartStub = styled('div')(({ theme }) => ({
  alignItems: 'center',
  background: theme.palette.secondary.dark,
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  justifyContent: 'center',
  padding: theme.spacing(1),
  [theme.breakpoints.down('xl')]: {
    width: '100%',
  },
}));

const ChartStatus = ({
  children,
  dimension = 'default',
  empty,
}: Pick<ChartContainerProps, 'children' | 'dimension' | 'empty'>) => {
  const { t } = useTranslation('overview');

  const stubStyle = {
    height: CHART_DIMENSIONS[dimension].height,
    width: CHART_DIMENSIONS[dimension].width,
  };

  if (empty)
    return (
      <ChartStub sx={(theme) => ({ background: theme.palette.secondary.dark, ...stubStyle })}>
        <BlockOutlined fontSize="large" />
        <Message>{t('empty.message')}</Message>
      </ChartStub>
    );

  return <>{children}</>;
};

export const ChartContainer = ({
  children,
  description,
  dimension,
  empty,
  title,
}: ChartContainerProps) => (
  <Container>
    <Title>{title}</Title>
    <Description>{description}</Description>
    <ChartStatus dimension={dimension} empty={empty}>
      {children}
    </ChartStatus>
  </Container>
);
